import type { Media } from "@/shared/lib/types"
import type { StagedMedia } from "./staged-media"

export type MediaKind = "photo" | "video" | "file"

/** Файл поста в одном виде, загружен он уже или только выбран. */
export interface MediaItem {
	key: string
	source: "existing" | "staged"
	kind: MediaKind
	url: string
	posterUrl: string | null
	name: string
	size: number | null
	width: number | null
	height: number | null
	duration: number | null
	existing?: Media
	staged?: StagedMedia
}

/** Служебные записи медиа, которые не показываются как вложения. */
const HIDDEN_TYPES = new Set(["preview", "video_thumb", "video_note_thumb"])

export function attachmentsOf(media: Media[] | undefined): Media[] {
	return (media ?? [])
		.filter((item) => !HIDDEN_TYPES.has(item.type))
		.sort((left, right) => left.order - right.order)
}

function kindOfType(type: string): MediaKind {
	if (type === "photo") return "photo"
	if (type === "video" || type === "video_note") return "video"
	return "file"
}

export function toMediaItems(existing: Media[], staged: StagedMedia[]): MediaItem[] {
	return [
		...existing.map<MediaItem>((media) => ({
			key: media.id,
			source: "existing",
			kind: kindOfType(media.type),
			url: media.url,
			posterUrl: media.type === "photo" ? media.url : (media.thumbnailUrl ?? null),
			name: media.filename || media.type,
			size: media.size ?? null,
			width: media.width ?? null,
			height: media.height ?? null,
			duration: media.duration ?? null,
			existing: media,
		})),
		...staged.map<MediaItem>((item) => ({
			key: item.key,
			source: "staged",
			kind: item.kind === "photo" ? "photo" : item.kind === "video" ? "video" : "file",
			url: item.objectUrl,
			posterUrl: item.kind === "photo" ? item.objectUrl : (item.poster?.objectUrl ?? null),
			name: item.file.name,
			size: item.file.size,
			width: item.width ?? null,
			height: item.height ?? null,
			duration: item.duration ?? null,
			staged: item,
		})),
	]
}

export function formatDuration(seconds: number | null): string | null {
	if (!seconds || seconds <= 0) return null
	const total = Math.round(seconds)
	const hours = Math.floor(total / 3600)
	const minutes = Math.floor((total % 3600) / 60)
	const rest = String(total % 60).padStart(2, "0")
	return hours ? `${hours}:${String(minutes).padStart(2, "0")}:${rest}` : `${minutes}:${rest}`
}

export function formatSize(bytes: number | null): string | null {
	if (!bytes) return null
	if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} КБ`
	if (bytes < 1024 * 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1).replace(".", ",")} МБ`
	return `${(bytes / 1024 / 1024 / 1024).toFixed(2).replace(".", ",")} ГБ`
}
